import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	HostListener,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewEncapsulation,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from "rxjs/operators";

import { AutocompleteGroup } from "@intocare/interfaces";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => GroupedAutoCompleteComponent),
	multi: true,
};

@Component({
	selector: "ic-grouped-autocomplete",
	templateUrl: "./grouped-autocomplete.component.html",
	providers: [INPUT_CONTROL_ACCESSOR],
	styleUrls: ["./grouped-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class GroupedAutoCompleteComponent<T> implements ControlValueAccessor, OnInit, OnDestroy {
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);
	private fb = inject(UntypedFormBuilder);

	@Input() icon: string;
	@Input() iconRight = false;
	@Input() loading = false;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() required = true;
	@Input() help: string;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() maxlength: string;
	@Input() threshold = 3;
	@Input() groups: AutocompleteGroup<T>[] = [];
	@Input() key: string;
	@Input() searchInputId: string;

	@HostBinding("class.error") @Input() error = false;
	@HostBinding("class.table-cell") @Input() isTableCell = false;

	@Output() optionSelected = new EventEmitter<T>();
	@Output() valueChange = new EventEmitter<string>();
	@Output() cleared = new EventEmitter<void>();

	@ContentChild("title", { static: false }) titleTemplate: TemplateRef<any>;
	@ContentChild("item", { static: false }) itemTemplate: TemplateRef<any>;
	@ContentChild("loading", { static: false }) loadingTemplate: TemplateRef<any>;
	@ContentChild("empty", { static: false }) emptyTemplate: TemplateRef<any>;
	@ContentChild("value", { static: false }) valueTemplate: TemplateRef<any>;

	showList = false;
	disabled = false;
	innerForm: UntypedFormGroup = this.fb.group({
		value: [""],
		input: [""],
	});

	private destroy$ = new Subject<void>();

	private onChange = (_: unknown) => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onTouch = () => {};

	@HostListener("document:click", ["$event"])
	clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target)) {
			this.showList = false;
		}
	}

	get value() {
		return this.innerForm.getRawValue().value;
	}

	selectOption(option: any) {
		const value = this.key ? option[this.key] : option;
		this.innerForm.patchValue({ value });

		this.showList = false;
		this.onTouch();
		this.onChange(this.innerForm.value.value);

		this.optionSelected.emit(this.value);
		this.innerForm.get("input").reset(undefined, { emitEvent: false });
		this.cdRef.markForCheck();
	}

	writeValue(value: any): void {
		if (value) {
			this.innerForm.patchValue({ value });
		} else {
			this.innerForm.reset(undefined, { emitEvent: false });
		}

		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;

		if (this.disabled) {
			this.innerForm.disable({ emitEvent: false });
		} else {
			this.innerForm.enable({ emitEvent: false });
		}
	}

	onFocus() {
		const { input } = this.innerForm.value;
		this.showList = this.threshold === 0 || (input && input.length > this.threshold);
	}

	clearForm() {
		this.cleared.emit();
	}

	ngOnInit() {
		this.innerForm
			.get("input")
			.valueChanges.pipe(
				distinctUntilChanged(),
				debounceTime(300),
				tap(() => {
					this.showList = false;
				}),
				filter((value) => !value || value.length >= this.threshold),
				tap((value) => {
					this.valueChange.emit(value);
					this.showList = true;
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
